import React, { FC, useMemo, createContext, useContext } from 'react';
import ReactDOM from 'react-dom/client';
import { ConnectionProvider, WalletProvider, useWallet, useAnchorWallet } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  TorusWalletAdapter,
  LedgerWalletAdapter,
} from '@solana/wallet-adapter-wallets';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { SOLANA_RPC_URL } from './config';
import {
  isDevMode,
  createDevWalletContext,
  getDevAnchorWallet,
  logDevWalletInfo,
} from './services/devWallet';

import 'bootstrap';
import toastr from 'toastr';
import '@solana/wallet-adapter-react-ui/styles.css';

// Set toastr on window for global access
window.toastr = toastr;

// Dev wallet context
const DevWalletContext = createContext<ReturnType<typeof createDevWalletContext> | null>(null);
const DevAnchorWalletContext = createContext<ReturnType<typeof getDevAnchorWallet> | null>(null);

// Custom hooks that use dev wallet when in dev mode
export const useDevWallet = () => {
  const devWallet = useContext(DevWalletContext);
  const realWallet = useWallet();

  if (isDevMode() && devWallet) {
    return devWallet;
  }
  return realWallet;
};

export const useDevAnchorWallet = () => {
  const devAnchorWallet = useContext(DevAnchorWalletContext);
  const realAnchorWallet = useAnchorWallet();

  if (isDevMode() && devAnchorWallet) {
    return devAnchorWallet;
  }
  return realAnchorWallet;
};

// Dev wallet provider
const DevWalletProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const devWalletContext = useMemo(() => createDevWalletContext(), []);
  const devAnchorWallet = useMemo(() => getDevAnchorWallet(), []);

  // Log dev wallet info on mount
  React.useEffect(() => {
    if (isDevMode()) {
      logDevWalletInfo();
    }
  }, []);

  return (
    <DevWalletContext.Provider value={devWalletContext}>
      <DevAnchorWalletContext.Provider value={devAnchorWallet}>
        {children}
      </DevAnchorWalletContext.Provider>
    </DevWalletContext.Provider>
  );
};

// Wallet context provider wrapper
const WalletContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'
  const network = process.env.NODE_ENV === 'development'
    ? WalletAdapterNetwork.Devnet
    : WalletAdapterNetwork.Devnet; // Change to Mainnet for production

  // Wallet adapters
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new TorusWalletAdapter(),
      new LedgerWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={SOLANA_RPC_URL}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <DevWalletProvider>
            {children}
          </DevWalletProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <WalletContextProvider>
    <App />
  </WalletContextProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
